import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Home,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  BarChart3,
  Users,
  Award,
  Bell,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const Navbar = ({ currentPage, onNavigate }) => {
  const { user, logout, isAdmin, displayName } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState(3); // Mock notification count

  // Add custom styles for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(10px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      @keyframes bounceGentle {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-5px);
        }
      }
      
      .animate-slideDown {
        animation: slideDown 0.3s ease-out;
      }
      
      .animate-fadeInUp {
        opacity: 0;
        animation: fadeInUp 0.5s ease-out forwards;
      }
      
      .animate-slideIn {
        animation: slideIn 0.3s ease-out;
      }
      
      .animate-fadeIn {
        animation: fadeIn 0.5s ease-out;
      }
      
      .animate-bounce-gentle {
        animation: bounceGentle 2s ease-in-out infinite;
      }
      
      .group:hover .group-hover\\:bg-opacity-20 {
        background-opacity: 0.2;
      }
      
      .group:hover .group-hover\\:bg-opacity-30 {
        background-opacity: 0.3;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Navigation items for regular users
  const userNavItems = [
    { id: 'dashboard', label: 'Inicio', icon: Home },
    { id: 'profile', label: 'Mi Perfil', icon: User },
  ];

  // Navigation items for admin users
  const adminNavItems = [
    { id: 'dashboard', label: 'Inicio', icon: Home },
    { id: 'admin', label: 'Administración', icon: Shield },
  ];

  const navItems = isAdmin ? adminNavItems : userNavItems;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close profile dropdown
      if (!event.target.closest('.profile-dropdown')) {
        setIsProfileDropdownOpen(false);
      }
      
      // Close mobile menu - improved logic
      if (isMobileMenuOpen && 
          !event.target.closest('.mobile-menu') && 
          !event.target.closest('.mobile-menu-button')) {
        setIsMobileMenuOpen(false);
      }
    };

    // Handle escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setIsProfileDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (pageId) => {
    onNavigate(pageId);
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  return (
    <nav className="bg-primary shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-3 animate-fadeIn">
            <div>
              <img 
                src="/logo.jpg" 
                alt="Puerto Madero Urubó" 
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white ring-opacity-20"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div 
                className="bg-white w-10 h-10 rounded-full flex items-center justify-center ring-2 ring-white ring-opacity-20 hidden"
                style={{display: 'none'}}
              >
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
            </div>
            {/* Title - Now shows on all screen sizes */}
            <div className="block">
              <h1 className="text-white text-base sm:text-lg font-bold">
                <span className="block sm:hidden">Universidad</span>
                <span className="hidden sm:block">Universidad Puerto Madero</span>
              </h1>
            </div>
          </div>

          {/* Desktop Navigation and User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Navigation Items */}
            <div className="flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 ${
                    currentPage === item.id
                      ? 'bg-white bg-opacity-20 text-white shadow-lg'
                      : 'text-blue-100 hover:bg-white hover:bg-opacity-10 hover:text-white'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Profile Dropdown */}
            <div className="relative profile-dropdown">
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10 transition-all transform hover:scale-105"
              >
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {displayName || `${user?.first_name} ${user?.last_name}`}
                  </p>
                  <p className="text-xs text-blue-200">
                    {isAdmin ? 'Administrador' : 'Estudiante'}
                  </p>
                </div>
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Desktop Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border animate-slideIn">
                  <div className="p-4 border-b border-gray-100">
                    <p className="font-medium text-gray-900">
                      {displayName || `${user?.first_name} ${user?.last_name}`}
                    </p>
                    <p className="text-sm text-gray-500">@{user?.username}</p>
                    {isAdmin && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary bg-opacity-10 text-primary mt-2">
                        <Shield className="w-3 h-3 mr-1" />
                        Administrador
                      </span>
                    )}
                  </div>
                  
                  <div className="py-2">
                    {/* Profile button only shows for admins */}
                    {isAdmin && (
                      <button
                        onClick={() => handleNavigation('profile')}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <User className="w-4 h-4 mr-3" />
                        Ver Perfil
                      </button>
                    )}
                    
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button with Animated Hamburger */}
          <div className="md:hidden">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className="mobile-menu-button p-2 text-white hover:text-white transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-dark focus:ring-opacity-50 rounded-lg relative z-50 border border-primary-dark border-opacity-40 hover:border-opacity-60 bg-transparent hover:bg-primary-dark hover:bg-opacity-20"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {/* Animated Hamburger to X */}
              <div className="relative w-6 h-6">
                {/* Top line */}
                <span 
                  className={`absolute h-0.5 w-6 bg-white transform transition-all duration-300 ${
                    isMobileMenuOpen 
                      ? 'rotate-45 translate-y-2.5' 
                      : 'rotate-0 translate-y-1'
                  }`}
                />
                {/* Middle line */}
                <span 
                  className={`absolute h-0.5 w-6 bg-white transform transition-all duration-300 ${
                    isMobileMenuOpen 
                      ? 'opacity-0 scale-0' 
                      : 'opacity-100 scale-100 translate-y-2.5'
                  }`}
                />
                {/* Bottom line */}
                <span 
                  className={`absolute h-0.5 w-6 bg-white transform transition-all duration-300 ${
                    isMobileMenuOpen 
                      ? '-rotate-45 translate-y-2.5' 
                      : 'rotate-0 translate-y-4'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Enhanced with Animations */}
        {isMobileMenuOpen && (
          <div className="md:hidden mobile-menu absolute left-0 right-0 top-full bg-primary-dark bg-opacity-95 backdrop-blur-md shadow-2xl border-t border-white border-opacity-20 z-50 animate-slideDown">
            <div className="bg-gradient-to-b from-primary-dark to-primary overflow-hidden shadow-inner">
              {/* Mobile User Info */}
              <div className="px-6 py-4 border-b border-blue-400 border-opacity-30 bg-white bg-opacity-5 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center ring-2 ring-white ring-opacity-30 animate-pulse">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-base truncate">
                      {displayName || `${user?.first_name} ${user?.last_name}`}
                    </p>
                    <p className="text-blue-200 text-sm">
                      {isAdmin ? 'Administrador' : 'Estudiante'}
                    </p>
                    {user?.username && (
                      <p className="text-blue-300 text-xs truncate mt-1">@{user.username}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 bg-opacity-20 text-yellow-200 border border-yellow-400 border-opacity-30">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </span>
                  )}
                </div>
              </div>

              {/* Mobile Navigation Items */}
              <div className="py-2">
                {navItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`w-full flex items-center space-x-4 px-6 py-4 transition-all duration-300 transform hover:scale-[0.98] animate-fadeInUp ${
                      currentPage === item.id
                        ? 'bg-white bg-opacity-20 text-white border-r-4 border-white shadow-lg'
                        : 'text-blue-100 hover:bg-white hover:bg-opacity-15 hover:text-white active:bg-white active:bg-opacity-25'
                    } focus:outline-none focus:bg-white focus:bg-opacity-15`}
                    style={{animationDelay: `${0.2 + index * 0.1}s`}}
                  >
                    <div className={`p-2 rounded-lg transition-all duration-200 ${
                      currentPage === item.id 
                        ? 'bg-white bg-opacity-20' 
                        : 'bg-white bg-opacity-10 group-hover:bg-opacity-20'
                    }`}>
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                    </div>
                    <span className="font-medium text-base">{item.label}</span>
                    {currentPage === item.id && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    )}
                  </button>
                ))}

                {/* Mobile Additional Options */}
                <div className="border-t border-blue-400 border-opacity-30 mt-3 pt-3">
                  {/* Profile option for admins only */}
                  {isAdmin && (
                    <button
                      onClick={() => handleNavigation('profile')}
                      className="w-full flex items-center space-x-4 px-6 py-4 text-blue-100 hover:bg-white hover:bg-opacity-15 hover:text-white transition-all duration-300 transform hover:scale-[0.98] focus:outline-none focus:bg-white focus:bg-opacity-15 animate-fadeInUp"
                      style={{animationDelay: `${0.2 + navItems.length * 0.1}s`}}
                    >
                      <div className="p-2 rounded-lg bg-white bg-opacity-10 group-hover:bg-opacity-20 transition-all duration-200">
                        <User className="w-5 h-5 flex-shrink-0" />
                      </div>
                      <span className="font-medium text-base">Ver Perfil</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleNavigation('settings')}
                    className="w-full flex items-center space-x-4 px-6 py-4 text-blue-100 hover:bg-white hover:bg-opacity-15 hover:text-white transition-all duration-300 transform hover:scale-[0.98] focus:outline-none focus:bg-white focus:bg-opacity-15 animate-fadeInUp"
                    style={{animationDelay: `${0.3 + navItems.length * 0.1}s`}}
                  >
                    <div className="p-2 rounded-lg bg-white bg-opacity-10 group-hover:bg-opacity-20 transition-all duration-200">
                      <Settings className="w-5 h-5 flex-shrink-0" />
                    </div>
                    <span className="font-medium text-base">Configuración</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-4 px-6 py-4 text-red-300 hover:bg-red-500 hover:bg-opacity-20 hover:text-red-200 transition-all duration-300 transform hover:scale-[0.98] focus:outline-none focus:bg-red-500 focus:bg-opacity-20 animate-fadeInUp"
                    style={{animationDelay: `${0.4 + navItems.length * 0.1}s`}}
                  >
                    <div className="p-2 rounded-lg bg-red-500 bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-200">
                      <LogOut className="w-5 h-5 flex-shrink-0" />
                    </div>
                    <span className="font-medium text-base">Cerrar Sesión</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;